import { z } from "zod";
import { createApiResponseSchema } from "../common/pagination";
import type { NotificationDelivery } from "./notification-delivery";

export const FormalityRequestSchema = z.object({
  id: z.string(),
  applicant_id: z.string(),
  code: z.string(),
  type: z.string(),
  name: z.string(),
  date: z.iso.datetime().nullable(),
  status: z.string(),
  status_label: z.string().optional(),
  description: z.string().nullable(),
  flow_execution_id: z.string().nullable(),
  flow_status: z.string().optional(),
  flowExecution: z.object({
    id: z.string(),
    flow_id: z.string(),
  }),
  group_id: z.string().nullable(),
  groups: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string().nullable(),
      pivot: z.object({
        assigned_user: z
          .object({
            id: z.string(),
            name: z.string(),
            last_name: z.string(),
            position: z
              .object({
                id: z.string(),
                name: z.string().optional(),
                nombre: z.string().optional(),
              })
              .optional(),
            email: z.email(),
          })
          .nullable(),
        assigned_at: z.iso.datetime().nullable(),
        created_at: z.iso.datetime().nullable(),
      }),
    }),
  ),
  applicant: z
    .object({
      id: z.string(),
      first_name: z.string(),
      last_name: z.string(),
      email: z.email(),
    })
    .nullable()
    .optional(),
  assignedTo: z
    .object({
      id: z.string(),
      name: z.string(),
      last_name: z.string(),
      position: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .optional(),
      email: z.email(),
    })
    .nullable(),
  process: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    status: z.string().optional(),
    three_code_identifier: z.string().nullable(),
    estimated_cost_min: z.number().nullable(),
    estimated_cost_max: z.number().nullable(),
    external_code: z.string().nullable(),
    estimated_time_min: z.number().nullable(),
    estimated_time_max: z.number().nullable(),
    estimated_time_min_unit: z.string().nullable(),
    estimated_time_max_unit: z.string().nullable(),
    estimated_steps_min: z.number().nullable(),
    estimated_steps_max: z.number().nullable(),
    created_at: z.iso.datetime().nullable(),
    required_documents: z.array(z.string()).nullable(),
    max_flows: z.number().nullable(),
    reason: z.string().nullable().optional(),
    created_by: z.string().nullable(),
    approved: z.boolean().nullable(),
    approved_at: z.iso.datetime().nullable(),
    updated_at: z.iso.datetime().nullable(),
    tags: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
        }),
      )
      .nullable(),
  }),
  created_at: z.iso.datetime().nullable(),
  updated_at: z.iso.datetime().nullable(),
  is_express: z.boolean().optional(),
  documents: z
    .array(
      z.looseObject({
        node_id: z.string(),
        field_id: z.string(),
        field_name: z.string().optional(),
        field_label: z.string().optional(),
      }), // Allow additional metadata properties
    )
    .optional(),
  first_assigned_at: z.iso.datetime().nullable(),
  cancellation_requests: z
    .array(
      z.object({
        id: z.string(),
        formality_request_id: z.string(),
        reason: z.string().nullable(),
        requested_by_id: z.string(),
        requested_by_type: z.string(),
        requested_at: z.iso.datetime(),
        status: z.string().nullable(),
        approved_at: z.iso.datetime().nullable(),
        denied_at: z.iso.datetime().nullable(),
        observation: z.string().nullable(),
        created_by: z.object({
          first_name: z.string().nullable().optional(),
          last_name: z.string().nullable().optional(),
        }),
      }),
    )
    .optional()
    .nullable(),
  notification_interventions: z
    .array(
      z.object({
        notification_dispatch_id: z.string(),
        node_execution_id: z.string(),
        node_id: z.string(),
        node_name: z.string().nullable(),
        status: z.object({ value: z.string(), label: z.string() }),
        last_error_kind: z.string().nullable(),
        last_error_message: z.string().nullable(),
        failed_deliveries: z.array(z.custom<NotificationDelivery>()),
      }),
    )
    .optional(),
  current_node: z
    .object({
      display_name: z.string(),
      id: z.string(),
      ref_name: z.string(),
      type: z.string(),
    })
    .optional()
    .nullable(),
  associated_expedient_registries: z
    .array(
      z.object({
        id: z.uuid(),
        unique_code: z.string(),
        name: z.string(),
        base_entity: z
          .object({
            id: z.string(),
            name: z.string(),
          })
          .optional()
          .nullable(),
      }),
    )
    .optional()
    .nullable(),
  has_associated_expedient_insolvencies: z.boolean().optional().nullable(),
  processor: z
    .object({
      id: z.string(),
      type: z.string(),
      name: z.string(),
    })
    .optional()
    .nullable(),
  holder: z
    .object({
      id: z.string(),
      type: z.string(),
      name: z.string(),
    })
    .optional()
    .nullable(),
  expired_associations: z
    .array(
      z.object({
        applicant_id: z.string(),
        applicant_name: z.string().optional(),
        authorization_ended_at: z.string().nullable().optional(),
      }),
    )
    .optional()
    .nullable(),
  elapsed_time_status: z
    .enum(["UNASSIGNED", "SCHEDULED", "COUNTING"])
    .optional()
    .nullable(),
  resolution: z
    .object({
      id: z.string().optional(),
      name: z.string().nullable().optional(),
      code: z.string().nullable().optional(),
      status: z.string().nullable().optional(),
      decision: z.string().nullable().optional(),
      comments: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
      files: z
        .array(
          z.object({
            id: z.string().optional(),
            file_name: z.string().optional(),
            formatted_file_name: z.string().optional(),
            name: z.string().optional(),
            size: z.number().optional(),
            url: z.string().optional(),
            file_url: z.string().optional(),
            link: z.string().optional(),
            path: z.string().optional(),
            is_signed: z.boolean().optional(),
          }),
        )
        .optional(),
    })
    .optional()
    .nullable(),
});

export type FormalityRequestType = z.infer<typeof FormalityRequestSchema>;

export const ApiFormalityRequestResponseSchema = createApiResponseSchema(
  FormalityRequestSchema,
);
export type ApiFormalityRequestResponseType = z.infer<
  typeof ApiFormalityRequestResponseSchema
>;
